import { useMutation } from '@tanstack/react-query'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { Platform, ScrollView, StyleSheet, View } from 'react-native'
import { Card, Text, useTheme } from 'react-native-paper'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { errorToString } from '@/lib/errors'
import { notify } from '@/lib/notice'
import { PB_SERVER_ADDR, pb } from '@/lib/pocketbase'
import { Button } from '@/ui/Button'
import { TextInput, useForm, z, zodResolver } from '@/ui/form'

const inviteAcceptSchema = z.object({
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .min(1, 'Password is required'),
})

type InviteAcceptFormData = z.infer<typeof inviteAcceptSchema>

type InviteAcceptResponse = {
    success: true
    token: string
    user: {
        id: string
        collectionId: string
        collectionName: string
        email: string
        name: string
        role: string
        expand: {
            org: {
                id: string
                name: string
                slug: string
            }
        }
    }
    org: {
        id: string
        name: string
        slug: string
    }
}

export default function InviteAcceptPage() {
    useAvoidSoftInputForScreen()
    const params = useLocalSearchParams<{ code?: string }>()
    const router = useRouter()
    const theme = useTheme()

    const semanticColors = useSemanticColors()
    const ContentWrapper = Platform.OS === 'ios' ? SafeAreaView : View

    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<InviteAcceptFormData>({
        resolver: zodResolver(inviteAcceptSchema),
        defaultValues: {
            password: '',
        },
    })

    const acceptInviteMutation = useMutation<InviteAcceptResponse, Error, InviteAcceptFormData>({
        mutationFn: async (data: InviteAcceptFormData) => {
            if (!params.code) {
                throw new Error('Invalid invitation link. No invitation code found.')
            }

            const response = await fetch(`${PB_SERVER_ADDR}/api/invite/accept`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    code: params.code,
                    password: data.password,
                }),
            })

            const result = await response.json()

            if (!response.ok) {
                throw new Error(result.message || 'Failed to accept invitation')
            }

            if (!result.success) {
                throw new Error(result.message || 'Failed to accept invitation')
            }
            return result as InviteAcceptResponse
        },
        onSuccess: (data: InviteAcceptResponse) => {
            if (data.token && data.user) {
                pb.authStore.save(data.token, data.user)
            }

            notify.success('Account created successfully!')

            setTimeout(() => {
                if (data.org?.slug) {
                    router.push(`/${data.org.slug}`)
                } else {
                    router.push('/')
                }
            }, 2000)
        },
        onError: error => {
            const message = errorToString(error)
            notify.error(message)
        },
    })

    const onSubmit = (data: InviteAcceptFormData) => {
        acceptInviteMutation.mutate(data)
    }

    if (acceptInviteMutation.isSuccess) {
        return (
            <ContentWrapper
                style={[styles.safeContainer, { backgroundColor: theme.colors.background }]}
            >
                <ScrollView style={styles.container}>
                    <View style={styles.contentContainer}>
                        <Text variant="headlineSmall" style={styles.pageTitle}>
                            Welcome!
                        </Text>
                        <Card
                            style={[
                                styles.successCard,
                                {
                                    backgroundColor: semanticColors.successLight,
                                    borderColor: semanticColors.successBorder,
                                },
                            ]}
                        >
                            <Card.Content style={styles.successContent}>
                                <Text
                                    variant="headlineSmall"
                                    style={[
                                        styles.successTitle,
                                        { color: semanticColors.successText },
                                    ]}
                                >
                                    Account Created Successfully!
                                </Text>
                                <Text
                                    variant="bodyMedium"
                                    style={{
                                        color: semanticColors.successText,
                                        textAlign: 'center',
                                    }}
                                >
                                    Your account has been created and you're now part of the team.
                                </Text>
                                <Text
                                    variant="bodySmall"
                                    style={{
                                        color: semanticColors.successText,
                                        textAlign: 'center',
                                    }}
                                >
                                    Redirecting to your dashboard...
                                </Text>
                            </Card.Content>
                        </Card>
                    </View>
                </ScrollView>
            </ContentWrapper>
        )
    }

    if (!params.code) {
        return (
            <ContentWrapper
                style={[styles.safeContainer, { backgroundColor: theme.colors.background }]}
            >
                <ScrollView style={styles.container}>
                    <View style={styles.contentContainer}>
                        <Text variant="headlineSmall" style={styles.pageTitle}>
                            Invalid Invitation
                        </Text>
                        <Card
                            style={[
                                styles.errorCard,
                                {
                                    backgroundColor: semanticColors.errorLight,
                                    borderColor: semanticColors.errorBorder,
                                },
                            ]}
                        >
                            <Card.Content>
                                <Text
                                    style={{ color: semanticColors.errorText, textAlign: 'center' }}
                                >
                                    This invitation link is invalid or incomplete.
                                </Text>
                            </Card.Content>
                        </Card>
                    </View>
                </ScrollView>
            </ContentWrapper>
        )
    }

    return (
        <ContentWrapper
            style={[styles.safeContainer, { backgroundColor: theme.colors.background }]}
        >
            <ScrollView style={styles.container}>
                <View style={styles.contentContainer}>
                    <Text variant="headlineSmall" style={styles.pageTitle}>
                        Accept Invitation
                    </Text>

                    <View style={styles.headerSection}>
                        <Text variant="headlineSmall" style={styles.headerTitle}>
                            Complete Your Account
                        </Text>
                        <Text
                            variant="bodyMedium"
                            style={{ textAlign: 'center', color: theme.colors.onSurfaceVariant }}
                        >
                            Set your password to complete your account setup
                        </Text>
                    </View>

                    {acceptInviteMutation.isError && (
                        <Card
                            style={[
                                styles.errorCard,
                                {
                                    backgroundColor: semanticColors.errorLight,
                                    borderColor: semanticColors.errorBorder,
                                },
                            ]}
                        >
                            <Card.Content>
                                <Text
                                    style={{ color: semanticColors.errorText, textAlign: 'center' }}
                                >
                                    {acceptInviteMutation.error instanceof Error
                                        ? acceptInviteMutation.error.message
                                        : 'Failed to accept invitation. Please try again.'}
                                </Text>
                            </Card.Content>
                        </Card>
                    )}

                    <Card style={styles.formCard}>
                        <Card.Content>
                            <View style={styles.formContent}>
                                <Text variant="titleMedium" style={styles.sectionTitle}>
                                    Create Your Password
                                </Text>

                                <View style={styles.inputSection}>
                                    <Text variant="labelLarge" style={styles.inputLabel}>
                                        Password *
                                    </Text>
                                    <TextInput
                                        name="password"
                                        control={control}
                                        placeholder="Create a password"
                                        secureTextEntry
                                        autoCapitalize="none"
                                        autoCorrect={false}
                                    />
                                    <Text
                                        variant="bodySmall"
                                        style={{ color: theme.colors.onSurfaceVariant }}
                                    >
                                        Password must be at least 8 characters long
                                    </Text>
                                </View>

                                <View style={styles.buttonSection}>
                                    <Button
                                        title="Create Account"
                                        mode="contained"
                                        onPress={handleSubmit(onSubmit)}
                                        disabled={acceptInviteMutation.isPending || !isValid}
                                        loading={acceptInviteMutation.isPending}
                                        loadingTitle="Creating Account..."
                                        style={styles.submitButton}
                                    />
                                </View>
                            </View>
                        </Card.Content>
                    </Card>
                </View>
            </ScrollView>
        </ContentWrapper>
    )
}

const styles = StyleSheet.create({
    safeContainer: {
        flex: 1,
    },
    container: {
        flex: 1,
    },
    contentContainer: {
        flex: 1,
        padding: 16,
        gap: 16,
        maxWidth: 600,
        alignSelf: 'center',
        width: '100%',
    },
    pageTitle: {
        fontWeight: 'bold',
    },
    headerSection: {
        alignItems: 'center',
        gap: 8,
    },
    headerTitle: {
        fontWeight: '600',
        textAlign: 'center',
    },
    successCard: {
        borderWidth: 1,
    },
    successContent: {
        alignItems: 'center',
        gap: 16,
    },
    successTitle: {
        fontWeight: '600',
        textAlign: 'center',
    },
    errorCard: {
        borderWidth: 1,
    },
    formCard: {
        borderWidth: 1,
    },
    formContent: {
        gap: 16,
    },
    sectionTitle: {
        fontWeight: '600',
        textAlign: 'center',
    },
    inputSection: {
        gap: 8,
    },
    inputLabel: {
        fontWeight: '600',
    },
    buttonSection: {
        marginTop: 8,
    },
    submitButton: {
        marginTop: 8,
    },
})
